import { makeApiRequest } from '@/src/lib/api-request'
import { useToken } from '@/src/store/token-store'
import { useUser } from '@/src/store/user-store'
import { Video } from '@/src/types/video'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

interface WatchlistVideoResponse {
  videos: Video[]
  offset: number
}

export const useWatchlistVideos = ({
  playerWatchlist,
}: {
  playerWatchlist: boolean
}) => {
  const { user } = useUser()
  const { accessToken } = useToken()
  return useInfiniteQuery({
    initialPageParam: { offset: 0 },
    queryKey: ['watchlist', 'watchlist-videos', user, accessToken],
    queryFn: async ({ pageParam }): Promise<WatchlistVideoResponse> => {
      const newOffset = pageParam.offset

      const dataProps = {
        limit: 8,
        offset: newOffset,
      }
      const response = await makeApiRequest({
        method: 'POST',
        url: '/Subscribe/getWatchlistData',
        isAuthenticated: true,
        data: dataProps,
        fallbackError: 'Error while fetching watchlist videos',
        isV3Api:true
      })
      const videos: Video[] = response.response.data?response.response.data:[]
      return { videos, offset: newOffset }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.videos?.length === 0) return undefined
      const newOffset = lastPage.offset + 8
      return { offset: newOffset }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    enabled: !!(user && accessToken),
  })
}

export const useAllWatchlistVideos = (isWatchlistEnabled: boolean) => {
  const { user } = useUser()
  const { accessToken } = useToken()
  return useQuery({
    queryKey: ['watchlist', 'watchlist-videos-all', user, accessToken],
    queryFn: async () => {
      const dataProps = {
        limit: -1,
        offset: 0,
      }
      const response = await makeApiRequest({
        method: 'POST',
        url: '/Subscribe/getWatchlistData',
        isAuthenticated: true,
        data: dataProps,
        fallbackError: 'Error while fetching watchlist videos',
        isV3Api:true
      })
      const videos: Video[] = response.response.data?response.response.data:[]
      return videos
    },
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    enabled: !!(user && accessToken) && isWatchlistEnabled,
  })
}

interface watchlistInfo {
  watchlistCount: string
  totalDuration: string
  createdAt: string
}

export const useWatchlistInfo = () => {
  const { user } = useUser()
  const { accessToken } = useToken()
  return useQuery({
    queryKey: ['watchlist', 'watchlist-info', user, accessToken],
    queryFn: async () => {
      const response = await makeApiRequest({
        method: 'GET',
        url: '/User/watchListInfo',
        isAuthenticated: true,
        data: {},
        fallbackError: 'Error while fetching watchlist videos',
        isV3Api:true
      })

      return response.response[0] as watchlistInfo
    },
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    enabled: !!(user && accessToken),
  })
}
